import React, { useState, ChangeEvent } from "react";
import { CustomTextFieldShadcn } from "@/dentlab/src/forms/form-input-components/custom-input-components/custom-text-field-shadcn.component";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { environment } from "../../../lib/utils/environment";
import { validatePasswordStrength, validateEmail, validateActivationCode } from "../onboarding-validations";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { Input } from "@/dentlab/src/components/ui/input";
import { OnboardingStep } from "../onboarding-components/onboarding-step";
import { Link } from "react-router-dom";
import { DevActivationCodeGenerator } from "./dev-activation-code-generator.component";
import { isUsingDevelopmentEnvironment } from "@/lib/utils/is-app-user";
import { Button } from "@/dentlab/src/components/ui/button";
import { StepProps } from "./types";

interface StepSignUpProps extends StepProps { }

export const StepSignUp: React.FC<StepSignUpProps> = ({ onComplete }) => {
    const [password, setPassword] = useState("");
    const [email, setEmail] = useState("");
    const [activationCode, setActivationCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState<{
        color: string;
        score: number;
        label: string;
    }>({
        color: "bg-green-500",
        score: 5,
        label: "Sehr stark",
    });

    const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
        const res = validatePasswordStrength(e.target.value);
        setPasswordStrength(res);
        setPassword(e.target.value);
    };

    const isValid =
        email.trim() !== "" &&
        validateEmail(email) &&
        password &&
        (passwordStrength?.score ?? 0) >= 4 &&
        activationCode.trim() !== "" &&
        validateActivationCode(activationCode);

    const handleCreateAccount = async () => {
        setLoading(true);
        setErrorMessage(null); // Clear any previous errors

        try {
            const res = await axios({
                method: "POST",
                url: `${environment.VITE_PDF_MICROSERVICE_URL}/register/lab-admin`,
                data: {
                    activation_code: activationCode,
                    email: email,
                    password: password,
                },
            });

            if (res.status === 200) {
                const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
                    email: email,
                    password: password,
                });

                if (authError || !authData.session) {
                    Logger.error(authError, {}, "Error signing in after registration");
                    setErrorMessage("Ein Fehler beim Anmelden ist aufgetreten");
                    setLoading(false);
                    return;
                }

                await useCentralStore.getState().initialize();
                onComplete();
                setLoading(false);
            }
        } catch (error: any) {
            if (error.code === "ERR_NETWORK") {
                setErrorMessage("Es konnte keine Verbindung zum Server hergestellt werden.");
                setLoading(false);
                return;
            }
            Logger.error("Error in signup", error);
            if (error.response?.data?.error === "Invalid activation code") {
                setErrorMessage("Ungültiger Aktivierungscode");
                setLoading(false);
                return;
            }
            if (error.response?.data?.message?.includes("email address has already")) {
                setErrorMessage("Diese E-Mail wird bereits verwendet");
                setLoading(false);
                return;
            }
            setErrorMessage("Ein Fehler ist aufgetreten");
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                handleCreateAccount();
            }}
            className="space-y-4"
        >
            <CustomTextFieldShadcn
                label="E-Mail"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                classNameInput="h-9 sm:h-10"
                placeholder="beispiel@email.ch"
                data-testid="signup-email-input"
            />
            <div>
                <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-sm font-medium leading-none">
                        Passwort <span className="text-destructive">*</span>
                    </span>
                </div>
                <div className="relative">
                    <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={handlePasswordChange}
                        className="h-9 sm:h-10 pr-10"
                        data-testid="signup-password-input"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                    >
                        {showPassword ? (
                            <Eye className="h-5 w-5" />
                        ) : (
                            <EyeOff className="h-5 w-5" />
                        )}
                    </button>
                </div>
            </div>

            {password && (
                <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-300 ${passwordStrength?.score === 0 ||
                                    passwordStrength?.score === 1
                                    ? "bg-red-500"
                                    : passwordStrength?.score === 2 ||
                                        passwordStrength?.score === 3
                                        ? "bg-yellow-500"
                                        : passwordStrength?.score === 4
                                            ? "bg-blue-500"
                                            : "bg-green-500"
                                    }`}
                                style={{
                                    width: `${(passwordStrength?.score ?? 0) * 20}%`,
                                }}
                            />
                        </div>
                        <div className="text-xs whitespace-nowrap text-gray-600">
                            {passwordStrength?.label}
                        </div>
                    </div>
                    <ul className="pl-4 text-xs list-disc text-gray-600">
                        <li>6 Zeichen</li>
                        <li>1 Grossbuchstabe</li>
                        <li>1 Zahl</li>
                        <li>1 Sonderzeichen</li>
                    </ul>
                </div>
            )}

            <CustomTextFieldShadcn
                label="Aktivierungscode"
                id="activationCode"
                name="activationCode"
                type="text"
                required
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                classNameInput="h-9 sm:h-10"
                customHelperText="Von dentDESK per E-Mail erhalten"
                data-testid="signup-activation-code-input"
            />

            {errorMessage && (
                <div className="rounded-md bg-red-50 p-4">
                    <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
            )}

            <Button
                type="submit"
                size="lg"
                className="w-full h-12 text-base"
                disabled={!isValid || loading}
                data-testid="signup-submit-button"
            >
                {loading ? "Wird geladen..." : "Registrieren"}
            </Button>

            <div className="mt-2 text-center">
                <Link
                    to="/"
                    className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
                >
                    Bereits registriert? Zum Login
                </Link>
            </div>

            {isUsingDevelopmentEnvironment(import.meta.env) && (
                <DevActivationCodeGenerator
                    onCodeGenerated={setActivationCode}
                    onEmailGenerated={setEmail}
                />
            )}
        </form>
    );
};